import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ORDERS_AUTO_CANCEL_PROMOTION } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useAppDispatch } from 'App/store';
import { useResponsive } from 'commons/components';
import { useGetCountry } from 'domains/Auth/hooks';
import {
  getOrdersAutoCancelConfiguration,
  getOrdersAutoCancelConfigurationData,
  getOrdersAutoCancelConfigurationStatus,
} from '../../ordersSlice';
import { useIsOrdersAutoCancelEnabledForUpsell } from '../useIsOrdersAutoCancelEnabled';

export function useShowOrdersAutoCancelPromotionalModal() {
  const dispatch = useAppDispatch();
  const config = useSelector(getOrdersAutoCancelConfigurationData);
  const { isMobile } = useResponsive();
  const [hasAutoCancelPromotionTag] = useHasTags(ORDERS_AUTO_CANCEL_PROMOTION);
  const storeCountry = useGetCountry();
  const isOrdersAutoCancelEnabledForUpsell =
    useIsOrdersAutoCancelEnabledForUpsell();
  const { isError, isLoading, isSuccess, isIdle } = useSelector(
    getOrdersAutoCancelConfigurationStatus,
  );

  const fetchOrdersAutoCancelConfiguration = useCallback(() => {
    dispatch(getOrdersAutoCancelConfiguration());
  }, [dispatch]);

  const isConfigurationInactiveOrNew = useMemo(() => {
    if (isLoading || isIdle || isError) {
      return false;
    }

    if (isSuccess) {
      if (!config?.isNew && config?.configuration) {
        const isAnyPaymentMethodActive = Object.values(
          config.configuration,
        ).some((paymentMethodConfig) => paymentMethodConfig?.isActive === true);
        return !isAnyPaymentMethodActive;
      } else {
        return true;
      }
    }

    return false;
  }, [isLoading, isIdle, isError, isSuccess, config]);

  const showModal = useMemo(
    () =>
      isOrdersAutoCancelEnabledForUpsell &&
      hasAutoCancelPromotionTag &&
      !isMobile &&
      isConfigurationInactiveOrNew &&
      storeCountry === 'BR',
    [
      isOrdersAutoCancelEnabledForUpsell,
      hasAutoCancelPromotionTag,
      isMobile,
      isConfigurationInactiveOrNew,
      storeCountry,
    ],
  );

  return {
    showPromotionalModal: showModal,
    fetchOrdersAutoCancelConfiguration,
  };
}
